import { ChangeEvent, memo, useEffect, useMemo, useState } from "react";
import { Tooltip } from "@mui/material";

import classNames from "common/class-names";

import AppTextareaDto from "dto/components/app-textarea-dto";

import RedTooltip from "assets/images/components/app-tooltip/tooltips-icon.svg";
import GreyTooltip from "assets/images/components/app-tooltip/tooltips-icon-dark-grey.svg";

const AppTextarea = (p: AppTextareaDto) => {
	const { error, touched, maxRows = 1, maxCharLength, placeholder, onChange, value, rows = 2, fieldRequired, tooltipContent, ...props } = p;

	const [textValue, setTextValue] = useState("");

	const [isTooltipOpen, setIsTooltipOpen] = useState(false);

	const isInvalid = useMemo(() => Boolean(error) && Boolean(touched), [error, touched]);

	const inputClassNames = useMemo(() => classNames({ "textarea__text-input": true, "textarea__text-input--error": isInvalid, "textarea__text-input--disabled": Boolean(props.disabled) }), [isInvalid, props.disabled]);

	const onHandleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
		const textareaLineHeight = 24;
		const previousRows = event.target.rows;
		event.target.rows = 1;

		const currentRows = Math.floor(event.target.scrollHeight / textareaLineHeight);

		if (currentRows === previousRows) {
			event.target.rows = currentRows;
		}

		if (currentRows >= maxRows) {
			event.target.rows = maxRows;

			event.target.scrollTop = event.target.scrollHeight;
		} else {
			event.target.rows = currentRows;
		}

		setTextValue(event.target.value);

		if (onChange) onChange(event);
	};

	useEffect(() => {
		if (value && !textValue) setTextValue(String(value));
	}, [textValue, value]);

	return (
		<div className="app-textarea">
			<div className="textarea">
				{props.label && (
					<label className="textarea__label">
						{props.label}

						{props.required && <span className="textarea__required"> *</span>}
					</label>
				)}

				<div className="textarea__container">
					<textarea className={inputClassNames} value={textValue} placeholder={placeholder} rows={rows} maxLength={maxCharLength} onChange={onHandleChange} />

					<div>
						{fieldRequired && <span className="textarea__required"> *</span>}

						{tooltipContent && (
							<Tooltip
								placement="right"
								title={tooltipContent}
								arrow
								componentsProps={{
									tooltip: {
										sx: {
											bgcolor: "#484964",
											fontSize: "11px",
											width: "250px",
											lineHeight: "16px",
											"& .MuiTooltip-arrow": {
												color: "#484964",
											},
										},
									},
								}}
								onOpen={() => setIsTooltipOpen(true)}
								onClose={() => setIsTooltipOpen(false)}
							>
								<img src={isTooltipOpen ? GreyTooltip : RedTooltip} alt="Toggle tooltip" />
							</Tooltip>
						)}
					</div>

					{!!maxCharLength && (
						<div className="textarea__maxCharLength">
							{value ? String(value).length : 0}/{maxCharLength}
						</div>
					)}
				</div>

				{isInvalid && <p className="textarea__text-error">{error}</p>}
			</div>
		</div>
	);
};

export default memo(AppTextarea);
